import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';
import {
  toast,
} from 'react-toastify';

import {
  api,
  coreSagas,
  coreActions,
  coreSelectors,
} from '@gymcloud/core';
import {
  history,
} from 'web-history';

import {
  ID,
} from 'web-utils';

import {
  getUiState,
} from 'web-selectors';

import {
  uiActions,
  actionTypes,
} from 'web-actions';

import createXMLHttpRequestChannel from './createXMLHttpRequestChannel';

function* uploadFileSaga({
  key,
  method,
  endpoint,
  formData,
}) {
  const channel = yield eff.call(
    createXMLHttpRequestChannel,
    {
      url: `${api.getApiUrl()}${endpoint}`,
      method,
      headers: {
        Authorization: `Bearer ${api.getAccessToken()}`,
      },
      formData,
    },
  );

  try {
    while (true) {
      const {
        progress = 0,
        err,
        success,
        result,
      } = yield eff.take(channel);

      if (err) {
        yield eff.put(
          uiActions.setFileUploadState({
            error: true,
          }, key),
        );
        return err;
      }

      if (success) {
        yield eff.put(
          uiActions.setFileUploadState({
            id: result.id,
            success,
          }, key),
        );
        return result;
      }
      yield eff.put(
        uiActions.setFileUploadState({
          progress,
        }, key),
      );
    }
  } finally {
    if (yield eff.cancelled()) {
      channel.close();
    }
  }
}

export default function* uploadFlow() {
  function* uploadVideos({ payload }) {
    const actions = createActionCreators('create', {
      resourceType: 'videos',
      requestKey: 'searchVideos',
      list: 'searchResults',
    });

    try {
      yield eff.put(actions.pending());

      const initial = yield eff.select(
        getUiState('fileUpload'),
      );
      const {
        fileUpload,
        uploadVideoModal,
        searchVideoModal,
      } = yield eff.select(
        getUiState([
          'fileUpload',
          'uploadVideoModal',
          'searchVideoModal',
        ]),
      );

      const {
        files,
        effects,
      } = payload.reduce((acc, file) => {
        const key = ID();
        const has = Object
          .values(initial)
          .some(f => f.name === file.name);

        const name = has ? `${file.name} (copy)` : file.name;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', name);

        return {
          files: {
            ...acc.files,
            [key]: {
              name,
              size: file.size,
              progress: 0,
            },
          },
          effects: [
            ...acc.effects,
            eff.call(
              uploadFileSaga,
              {
                key,
                endpoint: '/videos',
                formData,
              },
            ),
          ],
        };
      }, {
        files: fileUpload,
        effects: [],
      });

      yield eff.put(
        uiActions.setUiState({
          fileUpload: files,
          uploadVideoModal: {
            ...uploadVideoModal,
            isLoaded: false,
          },
        }),
      );

      const { results, cancel } = yield eff.race({
        results: eff.all(effects),
        cancel: eff.take(actionTypes.CANCEL_UPLOAD_FILES),
      });

      if (cancel) {
        yield eff.put(
          uiActions.setUiState({
            fileUpload: {},
            uploadVideoModal: {
              isOpen: false,
            },
          }),
        );
        yield eff.put(actions.succeeded({
          resources: [],
        }));
        return;
      }

      const resources = results.filter(v => v.id);

      yield eff.put(actions.succeeded({
        resources,
      }));

      if (searchVideoModal.isOpen) {
        yield eff.put(
          uiActions.onVideoUploadedSuccess(results),
        );
        // modal is closing to early
        yield eff.delay(1000);
        yield eff.put(
          uiActions.setUiState({
            fileUpload: {},
            searchVideoModal: {
              isOpen: false,
            },
            uploadVideoModal: {
              isOpen: false,
            },
          }),
        );
      } else {
        yield eff.put(
          uiActions.setUiState({
            uploadVideoModal: {
              ...uploadVideoModal,
              isLoaded: true,
            },
          }),
        );
      }

      toast.success(
        'Your video is being processed. You may check back later.',
      );
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(coreSagas.throwError, e);
    }
  }

  function* uploadDocuments({
    payload,
    folderId,
    documentId,
    callbacks,
  }) {
    const actions = createActionCreators('create', {
      resourceType: 'documents',
      requestKey: 'uploadDocuments',
      list: 'library',
    });

    try {
      yield eff.put(actions.pending());

      const rootFolderDocumentId = yield eff.select(
        coreSelectors.getFolderRoots('documentId'),
      );

      const initial = yield eff.select(
        getUiState('fileUpload'),
      );
      const {
        fileUpload,
        uploadFileModal,
      } = yield eff.select(
        getUiState([
          'fileUpload',
          'uploadFileModal',
        ]),
      );

      const {
        files,
        effects,
      } = payload.reduce((acc, file) => {
        const key = documentId || ID();
        const has = Object
          .values(initial)
          .some(f => f.name === file.name);

        const name = has ? `${file.name} (copy)` : file.name;

        const formData = new FormData();
        formData.append('document_file', file);
        formData.append('name', name);

        if (!documentId) {
          formData.append('folder_id', folderId || rootFolderDocumentId);
        }

        return {
          files: {
            ...acc.files,
            [key]: {
              name,
              size: file.size,
              progress: 0,
            },
          },
          effects: [
            ...acc.effects,
            eff.call(
              uploadFileSaga,
              {
                key,
                method: documentId ? 'PATCH' : 'POST',
                endpoint: documentId ? `/documents/${documentId}` : '/documents',
                formData,
              },
            ),
          ],
        };
      }, {
        files: fileUpload,
        effects: [],
      });

      yield eff.put(
        uiActions.setUiState({
          fileUpload: files,
          uploadFileModal: {
            ...uploadFileModal,
            isLoaded: false,
          },
        }),
      );

      const { results, cancel } = yield eff.race({
        results: eff.all(effects),
        cancel: eff.take(actionTypes.CANCEL_UPLOAD_FILES),
      });

      if (cancel) {
        yield eff.put(
          uiActions.setUiState({
            fileUpload: {},
            uploadFileModal: {
              isOpen: false,
            },
          }),
        );
        yield eff.put(actions.succeeded({
          resources: [],
        }));
        return;
      }

      const resources = results.filter(v => v.id);

      yield eff.put(actions.succeeded({
        resources,
      }));

      yield eff.put(
        uiActions.setUiState({
          uploadFileModal: {
            ...uploadFileModal,
            isLoaded: true,
          },
        }),
      );
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(coreSagas.throwError, e);
    }
  }

  function* uploadClientAvatar({
    payload,
    profileId,
  }) {
    const actions = createActionCreators('read', {
      resourceType: 'clients',
      requestKey: 'updateUserAvatar',
    });
    const formData = new FormData();
    formData.append('avatar', payload);

    try {
      yield eff.put(
        actions.pending(),
      );
      const userId = yield eff.select(
        coreSelectors.getProfile('id'),
      );

      yield eff.put(
        uiActions.setUiState({
          fileUpload: {
            [profileId]: {
              name: payload.name,
              size: payload.size,
              progress: 0,
            },
          },
        }),
      );

      const result = yield eff.call(
        uploadFileSaga,
        {
          key: profileId,
          method: 'PATCH',
          endpoint: `/user_profiles/${profileId}/avatar`,
          formData,
        },
      );

      if (userId === result.user_id) {
        yield eff.put(
          coreActions.setProfileState({
            user_profile: result,
          }),
        );
      }

      yield eff.put(
        actions.succeeded({
          resources: [{
            id: result.user_id,
            user_profile: result,
          }],
        }),
      );
    } catch (e) {
      yield eff.put(
        actions.succeeded({
          resources: [],
        }),
      );
      yield eff.call(coreSagas.throwError, e);
    }
  }

  function* uploadGroupAvatar({
    payload,
    groupId,
    callbacks,
  }) {
    const actions = createActionCreators('update', {
      resourceType: 'clientGroups',
      requestKey: 'updateGroupAvatar',
    });
    const formData = new FormData();
    formData.append('avatar', payload);

    try {
      yield eff.put(
        actions.pending(),
      );

      yield eff.put(
        uiActions.setUiState({
          fileUpload: {
            [groupId]: {
              name: payload.name,
              size: payload.size,
              progress: 0,
            },
          },
        }),
      );

      const result = yield eff.call(
        uploadFileSaga,
        {
          key: groupId,
          method: 'PATCH',
          endpoint: `/client_groups/${groupId}/avatar`,
          formData,
        },
      );

      if (callbacks?.onSuccess) {
        callbacks.onSuccess(result);
      }

      yield eff.put(
        actions.succeeded({
          resources: [{
            id: groupId,
            avatar: result.avatar,
          }],
        }),
      );
    } catch (e) {
      yield eff.put(
        actions.succeeded({
          resources: [],
        }),
      );

      if (callbacks?.onError) {
        callbacks.onError(e);
      }
      yield eff.call(coreSagas.throwError, e);
    }
  }

  function* sendMessage({
    payload,
    conversationId,
    callbacks,
  }) {
    const actions = createActionCreators('create', {
      resourceType: 'messages',
      requestKey: 'sendMessage',
    });
    const formData = new FormData();
    Object.keys(payload).forEach(key => {
      formData.append(key, payload[key]);
    });

    try {
      yield eff.put(
        actions.pending(),
      );

      const result = yield eff.call(
        uploadFileSaga,
        {
          key: ID(),
          method: 'POST',
          endpoint: conversationId !== 'new'
            ? `/conversations/${conversationId}`
            : '/conversations',
          formData,
        },
      );

      const includedResources = {};

      // TODO Refactor
      if (conversationId === 'new') {
        includedResources.conversations = yield eff.call(
          api.getConversations,
        );
      }

      callbacks?.onSuccess?.(result);

      yield eff.put(
        actions.succeeded({
          resources: [result],
          list: `${result.conversation_id}_${result.message_category}`,
          includedResources,
        }),
      );

      yield eff.put(
        uiActions.setUiState({
          fileUpload: {},
        }),
      );
    } catch (e) {
      yield eff.put(
        actions.succeeded({
          resources: [],
        }),
      );
      callbacks?.onError?.(e);

      yield eff.call(coreSagas.throwError, e);
    }
  }

  yield eff.takeEvery(actionTypes.REQUEST_SEND_MESSAGE, sendMessage);
  yield eff.takeEvery(actionTypes.REQUEST_UPLOAD_VIDEOS, uploadVideos);
  yield eff.takeEvery(actionTypes.REQUEST_UPLOAD_DOCUMENTS, uploadDocuments);

  yield eff.takeEvery(coreActions.actionTypes.REQUEST_UPLOAD_CLIENT_AVATAR, uploadClientAvatar);
  yield eff.takeEvery(coreActions.actionTypes.REQUEST_UPLOAD_GROUP_AVATAR, uploadGroupAvatar);
}
